/**
 * Role-based conditional rendering component.
 *
 * Shows children only if user has required role(s).
 */

import { ReactNode } from 'react';
import { useUserRole } from '../../hooks/useUserRole';
import type { DossierRole } from '../../types';

interface RoleGateProps {
  /** Children to render if role check passes */
  children: ReactNode;
  /** Required role(s) - user must have at least one */
  roles?: DossierRole[];
  /** Require at least this role level (uses hierarchy) */
  minRole?: DossierRole;
  /** Fallback to render if role check fails */
  fallback?: ReactNode;
  /** If true, hide completely instead of showing fallback */
  hideIfUnauthorized?: boolean;
}

/**
 * Conditionally renders children based on user role.
 *
 * @example
 * // Show only for system admins
 * <RoleGate roles={['system_admin']}>
 *   <AdminPanel />
 * </RoleGate>
 *
 * @example
 * // Show for group_admin or higher
 * <RoleGate minRole="group_admin">
 *   <GroupSettings />
 * </RoleGate>
 *
 * @example
 * // Show fallback for viewers
 * <RoleGate minRole="user" fallback={<ReadOnlyBadge />}>
 *   <EditButton />
 * </RoleGate>
 */
export function RoleGate({
  children,
  roles,
  minRole,
  fallback = null,
  hideIfUnauthorized = false,
}: RoleGateProps) {
  const { hasRoleOrHigher, roles: userRoles } = useUserRole();

  // Check role requirement
  let hasAccess = false;

  if (minRole) {
    // Use hierarchy check
    hasAccess = hasRoleOrHigher(minRole);
  } else if (roles && roles.length > 0) {
    // Check if user has any of the required roles
    hasAccess = roles.some((role) => userRoles.includes(role));
  } else {
    // No role requirements specified - always show
    hasAccess = true;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  if (hideIfUnauthorized) {
    return null;
  }

  return <>{fallback}</>;
}

/**
 * Shorthand for system admin only content.
 */
export function SystemAdminOnly({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <RoleGate roles={['system_admin']} fallback={fallback}>
      {children}
    </RoleGate>
  );
}

/**
 * Shorthand for group admin or higher content.
 */
export function GroupAdminOrHigher({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <RoleGate minRole="group_admin" fallback={fallback}>
      {children}
    </RoleGate>
  );
}

/**
 * Shorthand for non-viewer content (user role or higher).
 */
export function NonViewerOnly({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <RoleGate minRole="user" fallback={fallback}>
      {children}
    </RoleGate>
  );
}
